// src/services/projectScores.service.ts
import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';

export interface ScoreRecord {
  projectId: string;
  score: number;
}

export async function fetchScoresByProjectIds(
  projectIds: string[]
): Promise<Record<string, number[]>> {
  if (projectIds.length === 0) return {};

  // Firestore 'in' only accepts up to 10 items per query
  const chunks: string[][] = [];
  for (let i = 0; i < projectIds.length; i += 10) {
    chunks.push(projectIds.slice(i, i + 10));
  }

  const scoresByProject: Record<string, number[]> = {};

  // Fetch from old projectScores collection
  for (const chunk of chunks) {
    const q = query(
      collection(db, 'projectScores'),
      where('projectId', 'in', chunk)
    );
    const snap = await getDocs(q);
    snap.forEach((doc) => {
      const data = doc.data() as ScoreRecord;
      scoresByProject[data.projectId] = scoresByProject[data.projectId] || [];
      scoresByProject[data.projectId].push(data.score);
    });
  }

  return scoresByProject;
}

export function computeStats(
  scoresByProject: Record<string, number[]>
): Record<string, { averageScore: number | null; judgeCount: number }> {
  const stats: Record<
    string,
    { averageScore: number | null; judgeCount: number }
  > = {};
  for (const [projectId, scores] of Object.entries(scoresByProject)) {
    const judgeCount = scores.length;
    const averageScore = judgeCount
      ? scores.reduce((sum, s) => sum + s, 0) / judgeCount
      : null;
    stats[projectId] = { averageScore, judgeCount };
  }
  return stats;
}

export function subscribeToProjectScores(
  projectIds: string[],
  callback: (scoresByProject: Record<string, number[]>) => void
): () => void {
  if (projectIds.length === 0) {
    callback({});
    return () => {};
  }

  const scoresByProject: Record<string, number[]> = {};
  const unsubscribers: (() => void)[] = [];

  // helper to update and notify
  const updateAndNotify = () => {
    callback(scoresByProject);
  };

  // listen to project scores collection (now includes rubric scores)
  for (const projectId of projectIds) {
    const q = query(
      collection(db, 'projectScores'),
      where('projectId', '==', projectId)
    );
    const unsub1 = onSnapshot(q, (snapshot) => {
      scoresByProject[projectId] = scoresByProject[projectId] || [];
      scoresByProject[projectId] = [];

      snapshot.forEach((doc) => {
        const data = doc.data() as ScoreRecord;
        scoresByProject[projectId].push(data.score);
      });
      updateAndNotify();
    });
    unsubscribers.push(unsub1);
  }
  return () => {
    unsubscribers.forEach((unsub) => unsub());
  };
}
